import jsPDF from 'jspdf';
import { SignedContract, ContractTemplate, Language, DynamicField } from '@/shared/types';
import { drawRichHtml, htmlToPlainText, RichPdfCtx } from './richTextPdf';
import { substituteVariables, buildSubstitutionVars } from '@/shared/lib/templateVariables';

// ─── i18n ─────────────────────────────────────────────────────────────────────
const LABELS = {
  title:           { fr: 'FORMULAIRE DE CONSENTEMENT', en: 'CONSENT FORM', es: 'FORMULARIO DE CONSENTIMIENTO' },
  clientInfo:      { fr: 'INFORMATIONS CLIENT',        en: 'CLIENT INFORMATION',  es: 'INFORMACIÓN DEL CLIENTE' },
  healthInfo:      { fr: 'INFORMATIONS MÉDICALES',     en: 'HEALTH INFORMATION',  es: 'INFORMACIÓN DE SALUD' },
  procedureDetails:{ fr: 'DÉTAILS DE LA PROCÉDURE',    en: 'PROCEDURE DETAILS',   es: 'DETALLES DEL PROCEDIMIENTO' },
  legalAgreement:  { fr: 'CONSENTEMENT & ACCORDS',     en: 'CONSENT & AGREEMENTS',es: 'CONSENTIMIENTO Y ACUERDOS' },
  photos:          { fr: 'PHOTOS',                     en: 'PHOTOS',              es: 'FOTOS' },
  signatureArea:   { fr: 'SIGNATURE',                  en: 'SIGNATURE',           es: 'FIRMA' },
  clientSignature:       { fr: 'Signature du client',      en: 'Client Signature',       es: 'Firma del cliente' },
  practitionerSignature: { fr: 'Signature du praticien',   en: 'Practitioner Signature', es: 'Firma del profesional' },
  date:            { fr: 'Date',                       en: 'Date',                es: 'Fecha' },
  signedOn:        { fr: 'Signé le',                   en: 'Signed on',           es: 'Firmado el' },
  page:            { fr: 'Page',                       en: 'Page',                es: 'Página' },
  of:              { fr: 'sur',                        en: 'of',                  es: 'de' },
  none:            { fr: 'Aucune',                     en: 'None',                es: 'Ninguna' },
  yes:             { fr: 'Oui',                        en: 'Yes',                 es: 'Sí' },
  no:              { fr: 'Non',                        en: 'No',                  es: 'No' },
  generatedBy:     { fr: 'Généré par Consentink',      en: 'Generated by Consentink', es: 'Generado por Consentink' },
} as const;

const l = (key: keyof typeof LABELS, lang: Language): string => LABELS[key][lang];

// ─── Colors ───────────────────────────────────────────────────────────────────
const C = {
  headerDark:  [13,  23,  33]  as [number,number,number],  // #0d1721
  headerLight: [26,  58,  58]  as [number,number,number],  // #1a3a3a
  primary:     [26,  58,  58]  as [number,number,number],
  secondary:   [62, 100, 100]  as [number,number,number],
  accent:      [46, 184, 170]  as [number,number,number],  // #2eb8aa
  text:        [30,  30,  30]  as [number,number,number],
  muted:       [120,120, 120]  as [number,number,number],
  labelGray:   [150,150, 150]  as [number,number,number],
  light:       [248,250, 249]  as [number,number,number],
  border:      [215,225, 220]  as [number,number,number],
  white:       [255,255, 255]  as [number,number,number],
  rowEven:     [245,249, 247]  as [number,number,number],
};

// ─── PDF context ──────────────────────────────────────────────────────────────
interface Ctx {
  doc: jsPDF;
  y: number;
  lang: Language;
  W: number;
  H: number;
  M: number;
  CW: number;
  rowIdx: number;
}

function makeCtx(lang: Language): Ctx {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 16;
  return { doc, y: 0, lang, W, H, M, CW: W - M * 2, rowIdx: 0 };
}

// ─── Gradient helper (simulated via strips) ───────────────────────────────────
function drawGradientRect(
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  from: [number,number,number], to: [number,number,number],
) {
  const steps = 40;
  const stepW = w / steps;
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    const r = Math.round(from[0] + (to[0] - from[0]) * t);
    const g = Math.round(from[1] + (to[1] - from[1]) * t);
    const b = Math.round(from[2] + (to[2] - from[2]) * t);
    doc.setFillColor(r, g, b);
    doc.rect(x + i * stepW, y, stepW + 0.3, h, 'F');
  }
}

// ─── Page helpers ─────────────────────────────────────────────────────────────
let _pdfOptions: PdfOptions = {};

function ensureSpace(ctx: Ctx, needed: number) {
  if (ctx.y + needed > ctx.H - 22) {
    ctx.doc.addPage();
    drawPageHeader(ctx, '', _pdfOptions);
  }
}

function addFooters(ctx: Ctx, businessName: string, dateStr: string) {
  const brand = businessName || 'Consentink';
  const total = ctx.doc.getNumberOfPages();
  const footerY = ctx.H - 12;

  for (let i = 1; i <= total; i++) {
    ctx.doc.setPage(i);

    ctx.doc.setDrawColor(...C.border);
    ctx.doc.setLineWidth(0.3);
    ctx.doc.line(ctx.M, footerY, ctx.W - ctx.M, footerY);

    ctx.doc.setFontSize(7.5);
    ctx.doc.setFont('helvetica', 'normal');
    ctx.doc.setTextColor(...C.muted);

    ctx.doc.text(brand, ctx.M, footerY + 5);
    ctx.doc.text(
      `${l('page', ctx.lang)} ${i} ${l('of', ctx.lang)} ${total}`,
      ctx.W / 2, footerY + 5, { align: 'center' },
    );
    ctx.doc.text(dateStr, ctx.W - ctx.M, footerY + 5, { align: 'right' });
  }
}

// ─── Header ───────────────────────────────────────────────────────────────────
function drawPageHeader(ctx: Ctx, title: string, options: PdfOptions = {}) {
  const isFirst = title !== '';
  const headerH = isFirst ? 40 : 12;

  drawGradientRect(ctx.doc, 0, 0, ctx.W, headerH, C.headerDark, C.headerLight);

  // Teal accent line at bottom of header
  ctx.doc.setFillColor(...C.accent);
  ctx.doc.rect(0, headerH, ctx.W, 0.8, 'F');

  if (isFirst) {
    const { logoDataUrl, businessName } = options;
    const brandName = businessName?.trim() || 'Consentink';

    let contentX = ctx.M;

    if (logoDataUrl?.startsWith('data:image/')) {
      try {
        const fmt = logoDataUrl.includes('image/png') ? 'PNG' : 'JPEG';
        const maxW = 40;
        const maxH = 20;
        let lw = maxW;
        let lh = maxH;
        try {
          const props = ctx.doc.getImageProperties(logoDataUrl);
          const ratio = props.width / props.height;
          lw = maxH * ratio;
          if (lw > maxW) { lw = maxW; lh = maxW / ratio; }
          else { lh = maxH; }
        } catch { /* use defaults */ }
        const logoY = (headerH - lh) / 2;
        ctx.doc.addImage(logoDataUrl, fmt, ctx.M, logoY, lw, lh);
        contentX = ctx.M + lw + 6;
      } catch { /* fallback to text-only */ }
    }

    const midY = headerH / 2;

    // Business name
    ctx.doc.setFontSize(13);
    ctx.doc.setFont('helvetica', 'bold');
    ctx.doc.setTextColor(...C.white);
    ctx.doc.text(brandName, contentX, midY - 2);

    // Template title below brand
    ctx.doc.setFontSize(9.5);
    ctx.doc.setFont('helvetica', 'normal');
    ctx.doc.setTextColor(...C.accent);
    const maxTitleW = ctx.W - ctx.M - contentX - 2;
    let displayTitle = title;
    while (ctx.doc.getTextWidth(displayTitle) > maxTitleW && displayTitle.length > 10) {
      displayTitle = displayTitle.slice(0, -4) + '…';
    }
    ctx.doc.text(displayTitle, contentX, midY + 6);

    // Form type label — right-aligned, subtle
    ctx.doc.setFontSize(7);
    ctx.doc.setFont('helvetica', 'normal');
    ctx.doc.setTextColor(120, 190, 180);
    ctx.doc.text(l('title', ctx.lang), ctx.W - ctx.M, midY - 2, { align: 'right' });

    ctx.y = headerH + 8;
  } else {
    ctx.doc.setFontSize(7.5);
    ctx.doc.setFont('helvetica', 'italic');
    ctx.doc.setTextColor(150, 190, 185);
    ctx.doc.text(
      (options.businessName?.trim() || 'Consentink') + '  ·  ' + l('title', ctx.lang),
      ctx.M, headerH / 2 + 2,
    );
    ctx.y = headerH + 6;
  }
}

// ─── Section title ────────────────────────────────────────────────────────────
function drawSection(ctx: Ctx, title: string) {
  ensureSpace(ctx, 18);
  ctx.y += 5;
  ctx.rowIdx = 0;

  ctx.doc.setFontSize(9);
  ctx.doc.setFont('helvetica', 'bold');
  ctx.doc.setTextColor(...C.accent);
  ctx.doc.text(title.toUpperCase(), ctx.M, ctx.y);
  ctx.y += 3;

  ctx.doc.setDrawColor(...C.accent);
  ctx.doc.setLineWidth(0.4);
  ctx.doc.line(ctx.M, ctx.y, ctx.W - ctx.M, ctx.y);
  ctx.y += 5;
}

// ─── Label / value row ────────────────────────────────────────────────────────
function drawField(ctx: Ctx, label: string, value: string, halfWidth = false) {
  const val = (value || '').trim();
  if (!val || val === 'null' || val === 'undefined') return;

  const colW = halfWidth ? ctx.CW / 2 - 2 : ctx.CW;
  const labelW = Math.min(ctx.doc.getStringUnitWidth(label) * 8 / ctx.doc.internal.scaleFactor + 4, 50);
  const lines = ctx.doc.splitTextToSize(val, colW - labelW - 4);
  const rowH = Math.max(lines.length * 4.5, 5.5) + 3;

  ensureSpace(ctx, rowH + 2);

  if (ctx.rowIdx % 2 === 0) {
    ctx.doc.setFillColor(...C.rowEven);
    ctx.doc.rect(ctx.M, ctx.y - 3.5, ctx.CW, rowH, 'F');
  }
  ctx.rowIdx++;

  ctx.doc.setFontSize(8);
  ctx.doc.setFont('helvetica', 'bold');
  ctx.doc.setTextColor(...C.labelGray);
  ctx.doc.text(label, ctx.M + 2, ctx.y);

  ctx.doc.setFont('helvetica', 'normal');
  ctx.doc.setTextColor(...C.text);
  ctx.doc.text(lines, ctx.M + 2 + labelW, ctx.y);
  ctx.y += Math.max(lines.length * 4.5, 5.5) + 1.5;
}

// ─── Two-column layout helper ─────────────────────────────────────────────────
function drawTwoCol(ctx: Ctx, fields: { label: string; value: string }[]) {
  const colW = ctx.CW / 2 - 3;
  let col = 0;
  let rowStartY = ctx.y;

  for (const { label, value } of fields) {
    const val = (value || '').trim();
    if (!val || val === 'null' || val === 'undefined') continue;

    ensureSpace(ctx, 9);
    const x = ctx.M + (col === 0 ? 0 : colW + 6);

    if (col === 0) {
      if (ctx.rowIdx % 2 === 0) {
        ctx.doc.setFillColor(...C.rowEven);
        ctx.doc.rect(ctx.M, ctx.y - 3.5, ctx.CW, 10, 'F');
      }
      rowStartY = ctx.y;
    }

    ctx.doc.setFontSize(7.5);
    ctx.doc.setFont('helvetica', 'bold');
    ctx.doc.setTextColor(...C.labelGray);
    ctx.doc.text(label.toUpperCase(), x + 2, ctx.y);
    ctx.y += 4;

    ctx.doc.setFontSize(8.5);
    ctx.doc.setFont('helvetica', 'normal');
    ctx.doc.setTextColor(...C.text);
    const lines = ctx.doc.splitTextToSize(val, colW - 4);
    ctx.doc.text(lines, x + 2, ctx.y);

    if (col === 0) {
      ctx.y = rowStartY;
      col = 1;
    } else {
      ctx.y += lines.length * 4.5 + 4;
      ctx.rowIdx++;
      col = 0;
    }
  }
  if (col === 1) { ctx.y += 9; ctx.rowIdx++; }
}

// ─── Boolean field (Oui/Non) ──────────────────────────────────────────────────
function drawBoolean(ctx: Ctx, label: string, value: boolean) {
  ensureSpace(ctx, 8);

  ctx.doc.setFontSize(8);
  ctx.doc.setFont('helvetica', 'bold');
  ctx.doc.setTextColor(...C.labelGray);
  ctx.doc.text(label, ctx.M + 2, ctx.y);

  const startX = ctx.M + 2 + Math.min(ctx.doc.getTextWidth(label) + 4, 60);
  const boxSz = 3.5;
  const boxY = ctx.y - boxSz + 0.5;
  const lang = ctx.lang;

  if (value) {
    ctx.doc.setFillColor(...C.accent);
    ctx.doc.roundedRect(startX, boxY, boxSz, boxSz, 0.5, 0.5, 'F');
    drawTick(ctx, startX, boxY, boxSz);
  } else {
    ctx.doc.setDrawColor(...C.border);
    ctx.doc.setLineWidth(0.3);
    ctx.doc.roundedRect(startX, boxY, boxSz, boxSz, 0.5, 0.5, 'S');
  }
  ctx.doc.setFontSize(8);
  ctx.doc.setFont('helvetica', 'normal');
  ctx.doc.setTextColor(...C.text);
  ctx.doc.text(l('yes', lang), startX + boxSz + 1.5, ctx.y);

  const noX = startX + boxSz + 1.5 + ctx.doc.getTextWidth(l('yes', lang)) + 5;
  if (!value) {
    ctx.doc.setFillColor(...C.accent);
    ctx.doc.roundedRect(noX, boxY, boxSz, boxSz, 0.5, 0.5, 'F');
    drawTick(ctx, noX, boxY, boxSz);
  } else {
    ctx.doc.setDrawColor(...C.border);
    ctx.doc.setLineWidth(0.3);
    ctx.doc.roundedRect(noX, boxY, boxSz, boxSz, 0.5, 0.5, 'S');
  }
  ctx.doc.text(l('no', lang), noX + boxSz + 1.5, ctx.y);

  ctx.y += 6.5;
}

// ─── Draw a real tick mark (not unicode) ──────────────────────────────────────
function drawTick(ctx: Ctx, x: number, y: number, size: number) {
  ctx.doc.setDrawColor(...C.white);
  ctx.doc.setLineWidth(0.6);
  const pad = size * 0.2;
  ctx.doc.lines(
    [[size * 0.3, size * 0.4], [size * 0.5, -size * 0.55]],
    x + pad, y + size * 0.45,
  );
}

// ─── Checkbox ─────────────────────────────────────────────────────────────────
function drawCheckbox(ctx: Ctx, label: string, checked: boolean) {
  const lines = ctx.doc.splitTextToSize(label, ctx.CW - 14);
  const blockH = Math.max(lines.length * 4.8, 7) + 3;
  ensureSpace(ctx, blockH);

  const boxSz = 4.5;
  const boxX = ctx.M + 2;
  const boxY = ctx.y - boxSz + 0.8;

  if (checked) {
    ctx.doc.setFillColor(...C.accent);
    ctx.doc.roundedRect(boxX, boxY, boxSz, boxSz, 0.8, 0.8, 'F');
    drawTick(ctx, boxX, boxY, boxSz);
  } else {
    ctx.doc.setDrawColor(...C.border);
    ctx.doc.setLineWidth(0.4);
    ctx.doc.roundedRect(boxX, boxY, boxSz, boxSz, 0.8, 0.8, 'S');
  }

  ctx.doc.setFontSize(8.5);
  ctx.doc.setFont('helvetica', 'normal');
  ctx.doc.setTextColor(...C.text);
  ctx.doc.text(lines, ctx.M + 10, ctx.y);
  ctx.y += blockH;
}

// ─── Table field renderer ─────────────────────────────────────────────────────
function drawTable(ctx: Ctx, field: DynamicField, fieldValues: Record<string, string>, lang: Language) {
  const config = field.tableConfig;
  if (!config) return;

  const { columns, rows } = config;
  const nCols = columns.length;
  const colW = (ctx.CW - 2) / nCols;
  const rowH = 7;

  ensureSpace(ctx, (rows.length + 1) * rowH + 10);

  if (field.label[lang]) {
    ctx.doc.setFontSize(8.5);
    ctx.doc.setFont('helvetica', 'bold');
    ctx.doc.setTextColor(...C.labelGray);
    ctx.doc.text(field.label[lang], ctx.M + 2, ctx.y);
    ctx.y += 5;
  }

  const tableX = ctx.M + 1;

  ctx.doc.setFillColor(...C.primary);
  ctx.doc.rect(tableX, ctx.y, ctx.CW - 2, rowH, 'F');
  ctx.doc.setFontSize(7.5);
  ctx.doc.setFont('helvetica', 'bold');
  ctx.doc.setTextColor(...C.white);
  columns.forEach((col, ci) => {
    const text = ctx.doc.splitTextToSize(col[lang] || `Col ${ci + 1}`, colW - 4);
    ctx.doc.text(text[0] || '', tableX + ci * colW + 2, ctx.y + 4.5);
  });
  ctx.y += rowH;

  rows.forEach((row, ri) => {
    ensureSpace(ctx, rowH + 4);

    if (ri % 2 === 0) {
      ctx.doc.setFillColor(...C.rowEven);
      ctx.doc.rect(tableX, ctx.y, ctx.CW - 2, rowH, 'F');
    }

    ctx.doc.setDrawColor(...C.border);
    ctx.doc.setLineWidth(0.2);
    ctx.doc.rect(tableX, ctx.y, ctx.CW - 2, rowH, 'S');

    ctx.doc.setFontSize(8);
    ctx.doc.setFont('helvetica', 'normal');
    ctx.doc.setTextColor(...C.text);

    row.forEach((cell, ci) => {
      const cellKey = `${field.key}_r${ri}_c${ci}`;
      const cellValue = fieldValues[cellKey] || '';

      if (cell.type === 'checkbox') {
        const isChecked = cellValue === 'true';
        const boxX = tableX + ci * colW + 2;
        const boxY = ctx.y + 1.5;
        const bs = 3.5;
        if (isChecked) {
          ctx.doc.setFillColor(...C.accent);
          ctx.doc.roundedRect(boxX, boxY, bs, bs, 0.5, 0.5, 'F');
          drawTick(ctx, boxX, boxY, bs);
        } else {
          ctx.doc.setDrawColor(...C.border);
          ctx.doc.setLineWidth(0.3);
          ctx.doc.roundedRect(boxX, boxY, bs, bs, 0.5, 0.5, 'S');
        }
        if (cell.label?.[lang]) {
          ctx.doc.setFontSize(7.5);
          ctx.doc.setFont('helvetica', 'normal');
          ctx.doc.setTextColor(...C.text);
          ctx.doc.text(cell.label[lang], boxX + bs + 1.5, ctx.y + 4.5);
        }
      } else if (cell.type === 'static-text') {
        ctx.doc.setFontSize(7.5);
        ctx.doc.setTextColor(...C.muted);
        ctx.doc.text(cell.label?.[lang] || '', tableX + ci * colW + 2, ctx.y + 4.5);
      } else {
        if (cellValue) {
          const truncated = ctx.doc.splitTextToSize(cellValue, colW - 4)[0] || '';
          ctx.doc.text(truncated, tableX + ci * colW + 2, ctx.y + 4.5);
        }
      }

      if (ci < nCols - 1) {
        ctx.doc.setDrawColor(...C.border);
        ctx.doc.setLineWidth(0.2);
        ctx.doc.line(tableX + (ci + 1) * colW, ctx.y, tableX + (ci + 1) * colW, ctx.y + rowH);
      }
    });

    ctx.y += rowH;
  });

  ctx.y += 5;
}

// ─── Signatures block (side-by-side when ≥2) ─────────────────────────────────
function drawSignatures(ctx: Ctx, signatures: { label: string; dataUrl?: string }[], dateStr: string) {
  const sigList = signatures.length === 0
    ? [{ label: l('clientSignature', ctx.lang), dataUrl: undefined }]
    : signatures;

  const sigBoxH = 28;
  const totalH = sigBoxH + 18;
  ensureSpace(ctx, totalH);

  if (sigList.length >= 2) {
    const colW = ctx.CW / 2 - 3;
    const startY = ctx.y;

    for (let i = 0; i < Math.min(sigList.length, 2); i++) {
      const sig = sigList[i];
      const x = ctx.M + (i === 0 ? 0 : colW + 6);

      ctx.doc.setFontSize(7.5);
      ctx.doc.setFont('helvetica', 'bold');
      ctx.doc.setTextColor(...C.labelGray);
      ctx.doc.text(sig.label.toUpperCase(), x, startY);

      const boxY = startY + 3;
      ctx.doc.setDrawColor(...C.border);
      ctx.doc.setFillColor(...C.white);
      ctx.doc.setLineWidth(0.5);
      ctx.doc.roundedRect(x, boxY, colW, sigBoxH, 2.5, 2.5, 'FD');

      if (sig.dataUrl?.startsWith('data:image/')) {
        try {
          ctx.doc.addImage(sig.dataUrl, 'PNG', x + 2, boxY + 1, colW - 4, sigBoxH - 2);
        } catch { /* skip */ }
      } else {
        ctx.doc.setDrawColor(200, 215, 210);
        ctx.doc.setLineWidth(0.4);
        ctx.doc.line(x + 6, boxY + sigBoxH - 5, x + colW - 6, boxY + sigBoxH - 5);
      }

      ctx.doc.setFontSize(7);
      ctx.doc.setFont('helvetica', 'normal');
      ctx.doc.setTextColor(...C.muted);
      ctx.doc.text(dateStr, x, boxY + sigBoxH + 5);
    }
    ctx.y = startY + totalH;
  } else {
    const sig = sigList[0];
    const sigW = 95;

    ctx.doc.setFontSize(7.5);
    ctx.doc.setFont('helvetica', 'bold');
    ctx.doc.setTextColor(...C.labelGray);
    ctx.doc.text(sig.label.toUpperCase(), ctx.M, ctx.y);

    const boxY = ctx.y + 3;
    ctx.doc.setDrawColor(...C.border);
    ctx.doc.setFillColor(...C.white);
    ctx.doc.setLineWidth(0.5);
    ctx.doc.roundedRect(ctx.M, boxY, sigW, sigBoxH, 2.5, 2.5, 'FD');

    if (sig.dataUrl?.startsWith('data:image/')) {
      try {
        ctx.doc.addImage(sig.dataUrl, 'PNG', ctx.M + 2, boxY + 1, sigW - 4, sigBoxH - 2);
      } catch { /* skip */ }
    } else {
      ctx.doc.setDrawColor(200, 215, 210);
      ctx.doc.setLineWidth(0.4);
      ctx.doc.line(ctx.M + 8, boxY + sigBoxH - 5, ctx.M + sigW - 8, boxY + sigBoxH - 5);
    }

    ctx.doc.setFontSize(7);
    ctx.doc.setFont('helvetica', 'normal');
    ctx.doc.setTextColor(...C.muted);
    ctx.doc.text(dateStr, ctx.M, boxY + sigBoxH + 5);
    ctx.y = boxY + sigBoxH + 10;
  }
}

// ─── Photo ────────────────────────────────────────────────────────────────────
function drawPhoto(ctx: Ctx, label: string, dataUrl: string) {
  ensureSpace(ctx, 60);
  ctx.doc.setFontSize(8.5);
  ctx.doc.setFont('helvetica', 'bold');
  ctx.doc.setTextColor(...C.labelGray);
  ctx.doc.text(label, ctx.M + 2, ctx.y);
  ctx.y += 4;
  ctx.doc.setDrawColor(...C.border);
  ctx.doc.setLineWidth(0.3);
  ctx.doc.roundedRect(ctx.M + 2, ctx.y, 60, 48, 2, 2, 'S');
  try {
    const fmt = dataUrl.includes('image/png') ? 'PNG' : 'JPEG';
    ctx.doc.addImage(dataUrl, fmt, ctx.M + 4, ctx.y + 2, 56, 44);
  } catch { /* skip */ }
  ctx.y += 54;
}

// ─── Separator ────────────────────────────────────────────────────────────────
function sep(ctx: Ctx, light = false) {
  ensureSpace(ctx, 5);
  ctx.doc.setDrawColor(...(light ? [230, 238, 235] as [number,number,number] : C.border));
  ctx.doc.setLineWidth(light ? 0.2 : 0.3);
  ctx.doc.line(ctx.M, ctx.y, ctx.W - ctx.M, ctx.y);
  ctx.y += 4;
}

// ─── Address formatter ────────────────────────────────────────────────────────
function fmtAddress(raw: string): string | null {
  try {
    const obj = JSON.parse(raw);
    if (typeof obj !== 'object' || obj === null) return null;
    const parts: string[] = [];
    if (obj.street) parts.push(obj.street);
    if (obj.postalCode && obj.city) parts.push(`${obj.postalCode} ${obj.city}`);
    else if (obj.city) parts.push(obj.city);
    if (obj.department) parts.push(obj.department);
    if (obj.country) parts.push(obj.country);
    return parts.join(', ') || null;
  } catch { return null; }
}

// ─── JSON formatter ───────────────────────────────────────────────────────────
function fmtJson(raw: string): string | null {
  try {
    const obj = JSON.parse(raw);
    if (Array.isArray(obj)) return obj.join(', ');
    if (typeof obj === 'object' && obj !== null) {
      return Object.entries(obj)
        .filter(([, v]) => v !== null && v !== '' && v !== undefined)
        .map(([k, v]) => {
          const key = k.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ').trim();
          return `${key.charAt(0).toUpperCase()}${key.slice(1)}: ${v}`;
        }).join(' | ');
    }
  } catch { /* not json */ }
  return null;
}

// ─── Boolean detection ────────────────────────────────────────────────────────
function isBool(v: string) {
  const s = v.trim().toLowerCase();
  return ['true','false','oui','non','yes','no','sí'].includes(s);
}
function parseBool(v: string) {
  return ['true','oui','yes','sí'].includes(v.trim().toLowerCase());
}

// ─── Date formatter ───────────────────────────────────────────────────────────
function fmtDate(v: string, lang: Language) {
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return v;
    return d.toLocaleDateString(
      lang === 'fr' ? 'fr-FR' : lang === 'es' ? 'es-ES' : 'en-US',
      { year: 'numeric', month: 'long', day: 'numeric' },
    );
  } catch { return v; }
}

// ─── Health keys ──────────────────────────────────────────────────────────────
const HEALTH_KEYS = new Set(['ALLERGIES','ANTECEDENTS','TRAITEMENTS','GROSSESSE','MEDICAL','HEALTH','SANTE']);

// ─── Reserved client keys — case-insensitive, not re-rendered from template fields ─
// Covers both snake_case globals AND common uppercase template keys for the same data
const RESERVED_FIELD_KEYS_UPPER = new Set([
  'FIRST_NAME','LAST_NAME','EMAIL','PHONE',
  'NOM','PRENOM','NOM_COMPLET','NOM_CLIENT','PRENOM_CLIENT',
  'ADRESSE_EMAIL','TELEPHONE','TEL','MOBILE',
  'BIRTH_DATE','DATE_NAISSANCE','DATE_DE_NAISSANCE',
]);

// ─── Main export ─────────────────────────────────────────────────────────────
export interface PdfOptions {
  logoDataUrl?: string | null;
  businessName?: string | null;
}

export function generateContractPdf(
  contract: SignedContract,
  template: ContractTemplate | undefined,
  mode: 'save' | 'blob' = 'save',
  options: PdfOptions = {},
): void | Blob {
  const lang = contract.language;
  const ctx = makeCtx(lang);
  _pdfOptions = options;

  // ── Page 1 header ────────────────────────────────────────────────────────
  drawPageHeader(ctx, contract.templateName, options);

  // Date line
  const dateStr = fmtDate(contract.signedAt, lang);
  ctx.doc.setFontSize(8.5);
  ctx.doc.setFont('helvetica', 'italic');
  ctx.doc.setTextColor(...C.muted);
  ctx.doc.text(`${l('date', lang)}: ${dateStr}`, ctx.W - ctx.M, ctx.y, { align: 'right' });
  ctx.y += 10;

  // ── Sort fields according to template order ───────────────────────────────
  const fieldMap = new Map<string, DynamicField>(
    (template?.fields ?? []).map(f => [f.key, f]),
  );

  const tableKeyRx = /^(.+)_r(\d+)_c(\d+)$/;
  const allValueKeys = Object.keys(contract.fieldValues);
  const orderedFieldKeys: string[] = [];
  const templateFieldKeys = new Set<string>();

  if (template) {
    for (const f of template.fields) {
      orderedFieldKeys.push(f.key);
      templateFieldKeys.add(f.key);
      if (f.type === 'table' && f.tableConfig) {
        const rows = f.tableConfig.rows.length;
        const cols = f.tableConfig.columns.length;
        for (let r = 0; r < rows; r++)
          for (let c = 0; c < cols; c++)
            templateFieldKeys.add(`${f.key}_r${r}_c${c}`);
      }
    }
  }

  for (const k of allValueKeys) {
    if (!templateFieldKeys.has(k) && !tableKeyRx.test(k)) {
      orderedFieldKeys.unshift(k);
    }
  }

  // Reserved keys that also exist in the template would appear twice — deduplicate
  // using case-insensitive match. Keep only the first occurrence per canonical key.
  const seenReserved = new Set<string>();
  const deduped = orderedFieldKeys.filter(k => {
    const upper = k.toUpperCase();
    if (!RESERVED_FIELD_KEYS_UPPER.has(upper)) return true;
    if (seenReserved.has(upper)) return false;
    seenReserved.add(upper);
    return true;
  });

  // ── Classify fields ───────────────────────────────────────────────────────
  interface Item {
    label: string;
    value: string;
    fieldDef?: DynamicField;
    kind: 'text' | 'bool' | 'health-text' | 'health-bool' | 'photo' | 'signature' | 'table' | 'address' | 'textarea';
  }
  const clientItems: Item[] = [];
  const healthItems: Item[] = [];
  const photoItems: { label: string; dataUrl: string }[] = [];
  const signatures: { label: string; dataUrl: string }[] = [];
  const tableFields: DynamicField[] = [];
  const seenSigs = new Set<string>();

  for (const key of deduped) {
    const raw = contract.fieldValues[key];
    if (!raw || raw.trim() === '' || raw === 'null' || raw === 'undefined') continue;

    if (tableKeyRx.test(key)) continue;

    const field = fieldMap.get(key);
    const label = field?.label[lang] || (
      key === 'first_name' ? (lang === 'fr' ? 'Prénom' : lang === 'es' ? 'Nombre' : 'First Name') :
      key === 'last_name'  ? (lang === 'fr' ? 'Nom' : lang === 'es' ? 'Apellido' : 'Last Name') :
      key.replace(/^FIELD_\d+$/i, '').replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim() || key
    );

    if (raw.startsWith('data:image/')) {
      if (field?.type === 'signature' || key === 'SIGNATURE') {
        if (!seenSigs.has(raw)) { seenSigs.add(raw); signatures.push({ label, dataUrl: raw }); }
      } else {
        photoItems.push({ label, dataUrl: raw });
      }
      continue;
    }

    if (field?.type === 'table') {
      tableFields.push(field);
      continue;
    }

    if (field?.type === 'address') {
      const addr = fmtAddress(raw);
      if (addr) clientItems.push({ label, value: addr, kind: 'address', fieldDef: field });
      continue;
    }

    let display = fmtJson(raw) || raw;
    if (field?.type === 'date') display = fmtDate(raw, lang);

    const isHealth = HEALTH_KEYS.has(key) || (field?.key && HEALTH_KEYS.has(field.key));

    if (isBool(raw)) {
      const b = parseBool(raw);
      if (isHealth) healthItems.push({ label, value: raw, kind: 'health-bool', fieldDef: field, boolVal: b } as any);
      else clientItems.push({ label, value: raw, kind: 'bool', fieldDef: field, boolVal: b } as any);
      continue;
    }

    if (field?.type === 'textarea') continue;

    if (isHealth) {
      healthItems.push({ label, value: display, kind: 'health-text', fieldDef: field });
    } else {
      clientItems.push({ label, value: display, kind: 'text', fieldDef: field });
    }
  }

  // Main signature from contract.signatureDataUrl
  if (contract.signatureDataUrl?.startsWith('data:image/') && !seenSigs.has(contract.signatureDataUrl)) {
    seenSigs.add(contract.signatureDataUrl);
    signatures.push({ label: l('clientSignature', lang), dataUrl: contract.signatureDataUrl });
  }

  // Practitioner countersignature (added after cosign flow)
  if (contract.practitionerSignatureDataUrl?.startsWith('data:image/') && !seenSigs.has(contract.practitionerSignatureDataUrl)) {
    seenSigs.add(contract.practitionerSignatureDataUrl);
    signatures.push({ label: l('practitionerSignature', lang), dataUrl: contract.practitionerSignatureDataUrl });
  }

  // ── 1. Client info ────────────────────────────────────────────────────────
  if (clientItems.length > 0) {
    drawSection(ctx, l('clientInfo', lang));

    const shortItems = clientItems.filter(it => it.kind !== 'bool' && (it.value || '').length < 35);
    const longItems  = clientItems.filter(it => it.kind !== 'bool' && (it.value || '').length >= 35);
    const boolItems  = clientItems.filter(it => it.kind === 'bool');

    if (shortItems.length >= 2) {
      drawTwoCol(ctx, shortItems.map(it => ({ label: it.label, value: it.value })));
    } else {
      for (const it of shortItems) drawField(ctx, it.label, it.value);
    }
    for (const it of longItems) drawField(ctx, it.label, it.value);
    for (const it of boolItems) drawBoolean(ctx, it.label, !!(it as any).boolVal);
    ctx.y += 3;
  }

  // ── 2. Health ─────────────────────────────────────────────────────────────
  if (healthItems.length > 0) {
    sep(ctx, true);
    drawSection(ctx, l('healthInfo', lang));
    for (const it of healthItems) {
      if (it.kind === 'health-bool') drawBoolean(ctx, it.label, !!(it as any).boolVal);
      else drawField(ctx, it.label, it.value);
    }
    ctx.y += 3;
  }

  // ── 3. Tables ─────────────────────────────────────────────────────────────
  if (tableFields.length > 0) {
    sep(ctx, true);
    for (const tf of tableFields) {
      drawTable(ctx, tf, contract.fieldValues, lang);
    }
    ctx.y += 2;
  }

  // ── 4. Procedure sections (rich text) ─────────────────────────────────────
  if (template && template.sections.length > 0) {
    sep(ctx, true);
    drawSection(ctx, l('procedureDetails', lang));

    const readableVars: Record<string, string> = {};
    for (const [k, v] of Object.entries(contract.fieldValues)) {
      if (!v || v.startsWith('data:image/')) continue;
      readableVars[k] = fmtAddress(v) || fmtJson(v) || (fieldMap.get(k)?.type === 'date' ? fmtDate(v, lang) : v);
    }
    const subVars = buildSubstitutionVars({ fieldValues: readableVars, language: lang, signedAt: contract.signedAt });

    const richCtx: RichPdfCtx = {
      doc: ctx.doc,
      get y() { return ctx.y; },
      set y(v: number) { ctx.y = v; },
      margin: ctx.M,
      contentWidth: ctx.CW,
      pageHeight: ctx.H,
      ensureSpace: (n: number) => ensureSpace(ctx, n),
    } as unknown as RichPdfCtx;

    for (const section of template.sections) {
      ensureSpace(ctx, 14);
      ctx.doc.setFontSize(10);
      ctx.doc.setFont('helvetica', 'bold');
      ctx.doc.setTextColor(...C.accent);
      ctx.doc.text(section.title[lang] || '', ctx.M + 2, ctx.y);
      ctx.y += 6;

      const content = substituteVariables(section.content[lang] || '', subVars);
      drawRichHtml(richCtx, content, { text: C.text, primary: C.accent, baseFontSize: 9 });
      ctx.y += 4;
    }
  }

  // ── 5. Checkboxes ─────────────────────────────────────────────────────────
  if (template && template.checkboxes.length > 0) {
    sep(ctx);
    drawSection(ctx, l('legalAgreement', lang));

    const labelVars: Record<string, string> = {};
    for (const [k, v] of Object.entries(contract.fieldValues)) {
      if (!v || v.startsWith('data:image/')) continue;
      labelVars[k] = fmtAddress(v) || fmtJson(v) || v;
    }
    const cbVars = buildSubstitutionVars({ fieldValues: labelVars, language: lang, signedAt: contract.signedAt });

    for (const cb of template.checkboxes) {
      const raw = substituteVariables(cb.label[lang] || '', cbVars, { escape: false });
      drawCheckbox(ctx, htmlToPlainText(raw), !!contract.checkboxValues[cb.id]);
    }
    ctx.y += 4;
  }

  // ── 6. Photos ─────────────────────────────────────────────────────────────
  if (photoItems.length > 0) {
    sep(ctx);
    drawSection(ctx, l('photos', lang));
    for (const p of photoItems) drawPhoto(ctx, p.label, p.dataUrl);
  }

  // ── 7. Signatures — keep on current page if enough room ──────────────────
  // Total needed: sep(4) + section title(18) + sig boxes(~46)
  const sigTotalH = 70;
  const spaceLeft = ctx.H - 22 - ctx.y;
  if (spaceLeft < sigTotalH) {
    ctx.doc.addPage();
    drawPageHeader(ctx, '', _pdfOptions);
  } else {
    sep(ctx);
  }
  drawSection(ctx, l('signatureArea', lang));
  drawSignatures(ctx, signatures, dateStr);

  // ── Footer (all pages) ────────────────────────────────────────────────────
  addFooters(ctx, options.businessName?.trim() || 'Consentink', dateStr);

  if (mode === 'blob') return ctx.doc.output('blob');

  const safeName = (contract.clientName || 'contrat').replace(/[^a-zA-Z0-9]/g, '_');
  ctx.doc.save(`${safeName}_${contract.signedAt.slice(0, 10)}.pdf`);
}
